import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { getCroppedImage } from '@/lib/image-utils';

interface AdminCropModalProps {
  imageSrc: string;
  open: boolean;
  aspect?: number;
  onComplete: (croppedDataUrl: string) => void;
  onCancel: () => void;
}

const AdminCropModal: React.FC<AdminCropModalProps> = ({
  imageSrc,
  open,
  aspect = 16 / 9,
  onComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const cropped = await getCroppedImage(imageSrc, croppedAreaPixels);
      onComplete(cropped);
    } catch {
      onComplete(imageSrc);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (): void => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-[700px] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-black/[0.06]">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-black/50">
                Adjust Cover Photo
              </h3>
              <button
                onClick={onCancel}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-black/30 hover:text-black hover:bg-black/[0.04] transition-all"
              >
                <X size={14} />
              </button>
            </div>

            {/* Crop Area */}
            <div className="relative w-full h-[400px] bg-black/95">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={true}
                style={{
                  containerStyle: { borderRadius: 0 },
                  cropAreaStyle: { border: '2px solid rgba(255,255,255,0.6)' },
                }}
              />
            </div>

            {/* Controls */}
            <div className="px-5 py-4 border-t border-black/[0.06]">
              <div className="flex items-center gap-4 mb-4">
                <ZoomOut size={14} className="text-black/30 shrink-0" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-1 bg-black/10 rounded-full appearance-none cursor-pointer accent-black"
                />
                <ZoomIn size={14} className="text-black/30 shrink-0" />
                <button
                  onClick={handleReset}
                  className="ml-2 w-7 h-7 rounded-lg flex items-center justify-center text-black/30 hover:text-black hover:bg-black/[0.04] transition-all"
                  title="Reset"
                >
                  <RotateCcw size={13} />
                </button>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-black/40 hover:text-black/70 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-black text-white text-[10px] font-bold uppercase tracking-[0.1em] rounded-xl hover:bg-black/80 transition-colors disabled:opacity-50"
                >
                  <Check size={12} />
                  {saving ? 'Saving...' : 'Apply Crop'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminCropModal;
